import {create} from 'zustand';
import {PaymentFilterParams} from '../models/Payment';

interface PaymentsUIState {
  // Tab Management
  activeTab: 'payments' | 'transfers' | 'statistics';
  setActiveTab: (tab: 'payments' | 'transfers' | 'statistics') => void;

  // Filter State
  filters: PaymentFilterParams;
  showFilters: boolean;
  setFilters: (filters: Partial<PaymentFilterParams>) => void;
  setShowFilters: (show: boolean) => void;
  clearFilters: () => void;

  // Bank Transfer Review State
  transferNotes: Record<string, string>;
  actionLoading: {
    approve: boolean;
    reject: boolean;
  };
  confirmDialog: {
    open: boolean;
    type: 'approve' | 'reject' | null;
    invoiceId: number | string | null;
    invoiceNumber: string | null;
  };

  // Bank Transfer Actions
  setTransferNotes: (notes: Record<string, string>) => void;
  updateTransferNote: (invoiceId: string, note: string) => void;
  setActionLoading: (loading: Partial<PaymentsUIState['actionLoading']>) => void;
  setConfirmDialog: (dialog: Partial<PaymentsUIState['confirmDialog']>) => void;
  openConfirmDialog: (type: 'approve' | 'reject', invoiceId: number | string, invoiceNumber: string) => void;
  closeConfirmDialog: () => void;

  // Utility Actions
  reset: () => void;
}

const initialFilters: PaymentFilterParams = {
  search: '',
  paymentMethod: undefined,
  dateFrom: '',
  dateTo: '',
  customerName: ''
};

export const usePaymentsUIStore = create<PaymentsUIState>((set) => ({
  // Tab Management
  activeTab: 'payments',
  setActiveTab: (tab) => set({activeTab: tab}),

  // Filter State
  filters: initialFilters,
  showFilters: false,
  setFilters: (newFilters) => set((state) => ({
    filters: {...state.filters, ...newFilters}
  })),
  setShowFilters: (show) => set({showFilters: show}),
  clearFilters: () => set({filters: initialFilters}),

  // Bank Transfer Review State
  transferNotes: {},
  actionLoading: {
    approve: false,
    reject: false
  },
  confirmDialog: {
    open: false,
    type: null,
    invoiceId: null,
    invoiceNumber: null
  },

  // Bank Transfer Actions
  setTransferNotes: (notes) => set({transferNotes: notes}),
  updateTransferNote: (invoiceId, note) => set((state) => ({
    transferNotes: {...state.transferNotes, [invoiceId]: note}
  })),
  setActionLoading: (loading) => set((state) => ({
    actionLoading: {...state.actionLoading, ...loading}
  })),
  setConfirmDialog: (dialog) => set((state) => ({
    confirmDialog: {...state.confirmDialog, ...dialog}
  })),
  openConfirmDialog: (type, invoiceId, invoiceNumber) => set({
    confirmDialog: {
      open: true,
      type,
      invoiceId,
      invoiceNumber
    }
  }),
  closeConfirmDialog: () => set({
    confirmDialog: {
      open: false,
      type: null,
      invoiceId: null,
      invoiceNumber: null
    }
  }),

  // Utility Actions
  reset: () => set({
    activeTab: 'payments',
    filters: initialFilters,
    showFilters: false,
    transferNotes: {},
    actionLoading: {
      approve: false,
      reject: false
    },
    confirmDialog: {
      open: false,
      type: null,
      invoiceId: null,
      invoiceNumber: null
    }
  })
}));
